import type { ChangeEvent, CSSProperties, FC, ReactNode } from 'react'
import type { LayerId } from '@/constants/live-model'
import type { ViewerMeasure } from '@/lib/cloud/live-viewer'

import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { Minus, Plus, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'

import { LAYERS, LIVE_BUILDINGS } from '@/constants/live-model'

interface ViewerHandle {
    setBuilding: (shape: string, flags: Array<{ id: string, text: string, anchor: string }>) => void
    setLayer: (layer: LayerId, on: boolean) => void
    setCut: (value: number | null) => void
    setEpoch: (index: number) => void
    setMeasureMode: (on: boolean) => void
    clearMeasure: () => void
    focusFlag: (index: number | null) => void
    zoomBy: (factor: number) => void
    dispose: () => void
}

const delay = (value: number): CSSProperties => ({ animationDelay: `${value}ms` })

const SWATCH: Record<LayerId, string> = {
    structure: 'bg-ink',
    thermal: 'bg-ember',
    occupancy: 'bg-signal',
    services: 'border border-signal'
}

const LiveModelPage: FC = (): ReactNode => {
    const router = useRouter()
    const stageRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)
    const measureLabelRef = useRef<HTMLSpanElement>(null)
    const yawRef = useRef<HTMLSpanElement>(null)
    const pitchRef = useRef<HTMLSpanElement>(null)
    const zoomRef = useRef<HTMLSpanElement>(null)
    const viewerRef = useRef<ViewerHandle | null>(null)
    const [buildingIndex, setBuildingIndex] = useState(0)
    const [layers, setLayers] = useState<Record<LayerId, boolean>>({ structure: true, thermal: false, occupancy: false, services: false })
    const [cutOn, setCutOn] = useState(false)
    const [cut, setCut] = useState(35)
    const [epoch, setEpoch] = useState(3)
    const [measureMode, setMeasureMode] = useState(false)
    const [measure, setMeasure] = useState<ViewerMeasure>({ step: 0, millimetres: null })
    const [focused, setFocused] = useState<number | null>(null)
    const [failed, setFailed] = useState(false)
    const [ready, setReady] = useState(false)
    const building = LIVE_BUILDINGS[buildingIndex]

    useEffect(() => {
        if (!router.isReady) return

        const slug = typeof router.query.building === 'string' ? router.query.building : ''
        const index = LIVE_BUILDINGS.findIndex(item => item.slug === slug)

        if (index >= 0) setBuildingIndex(index)
    }, [router.isReady, router.query.building])

    useEffect(() => {
        const container = stageRef.current
        const labelRoot = labelRef.current
        const measureLabel = measureLabelRef.current

        if (!container || !labelRoot || !measureLabel) return

        let cancelled = false

        import('@/lib/cloud/live-viewer')
            .then(({ LiveViewer }) => {
                if (cancelled) return

                try {
                    viewerRef.current = new LiveViewer({
                        container,
                        labelRoot,
                        measureLabel,
                        onFrame: frame => {
                            if (yawRef.current) yawRef.current.textContent = String(Math.round(frame.yaw) % 360).padStart(3, '0')
                            if (pitchRef.current) pitchRef.current.textContent = String(Math.round(frame.pitch)).padStart(2, '0')
                            if (zoomRef.current) zoomRef.current.textContent = `${frame.zoom.toFixed(1)}×`
                        },
                        onMeasure: setMeasure
                    })
                    setReady(true)
                }
                catch (error) {
                    console.error('The live model could not start:', error instanceof Error ? error.message : error)
                    setFailed(true)
                }
            })
            .catch(() => setFailed(true))

        return () => {
            cancelled = true
            viewerRef.current?.dispose()
            viewerRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!ready) return

        viewerRef.current?.setBuilding(building.shape, building.flags)
        setFocused(null)
        setMeasure({ step: 0, millimetres: null })
    }, [ready, building])

    useEffect(() => {
        viewerRef.current?.setCut(cutOn ? cut / 100 : null)
    }, [ready, cutOn, cut])

    useEffect(() => {
        viewerRef.current?.setEpoch(epoch)
    }, [ready, epoch])

    useEffect(() => {
        viewerRef.current?.setMeasureMode(measureMode)
    }, [ready, measureMode])

    const toggleLayer = (id: LayerId): void => {
        const next = !layers[id]

        setLayers(current => ({ ...current, [id]: next }))
        viewerRef.current?.setLayer(id, next)
    }

    const focusFlag = (index: number): void => {
        const next = focused === index ? null : index

        setFocused(next)
        viewerRef.current?.focusFlag(next)
    }

    const epochInfo = building.epochs[epoch]

    return (
        <Layout>
            <Seo
                title='Live model'
                description='Explore a live Norrvik model: switch between Hallmark Library, Casa Mares and Ostra Cultural Hall, turn on structure, thermal, occupancy and services layers, cut a section, measure, and step through four dated scans.'
                figure='Fig. 01 / Live model / Interactive viewer'
                shape={building.shape}
            />

            <section aria-labelledby='live-title' className='relative border-b border-hairline'>
                <div
                    ref={stageRef}
                    role='img'
                    aria-label={`${building.label} Use the panels to switch layers, cut a section, measure and step through the scans.`}
                    data-crosshair=''
                    className={cn('relative h-[62svh] min-h-[420px] w-full touch-pan-y select-none overflow-hidden bg-background desk:h-[calc(100svh-4rem)] desk:min-h-[640px]', measureMode ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing')}
                >
                    <div ref={labelRef} className='cf-labels z-[1]' aria-hidden='true' />

                    <span
                        ref={measureLabelRef}
                        className='hud pointer-events-none absolute left-0 top-0 z-[3] border border-signal bg-background px-2 py-1 text-signal'
                        style={{ visibility: 'hidden' }}
                        aria-hidden='true'
                    />

                    <div className='pointer-events-none absolute inset-x-gutter top-5 z-[2] flex justify-center desk:inset-x-[340px]' aria-hidden='true'>
                        <p className='hud text-mute'>
                            Yaw <span ref={yawRef} className='text-ink'>032</span> / Pitch <span ref={pitchRef} className='text-ink'>26</span> / Zoom <span ref={zoomRef} className='text-ink'>1.0×</span>
                        </p>
                    </div>

                    <div className='pointer-events-none absolute inset-x-gutter bottom-5 z-[2] flex flex-wrap items-end justify-center gap-x-6 gap-y-2 desk:inset-x-[340px]' aria-hidden='true'>
                        <p className='hud text-mute'>
                            {measureMode ? 'Click two points to measure' : 'Drag to orbit'}
                        </p>

                        <p className='hud text-mute'>
                            Scan {epochInfo.date}
                        </p>

                        {epoch > 0 && epoch < 3 && (
                            <p className='hud text-mute'>
                                Deformation shown at 50×
                            </p>
                        )}
                    </div>

                    <div className='absolute bottom-4 right-gutter z-[2] flex flex-col border border-hairline bg-background desk:right-[340px]'>
                        <button type='button' onClick={() => viewerRef.current?.zoomBy(0.85)} className='grid size-9 place-items-center text-ink hover:text-signal' aria-label='Zoom in'>
                            <Plus className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        </button>

                        <button type='button' onClick={() => viewerRef.current?.zoomBy(1.18)} className='grid size-9 place-items-center border-t border-hairline text-ink hover:text-signal' aria-label='Zoom out'>
                            <Minus className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        </button>
                    </div>

                    {failed && (
                        <div className='absolute inset-0 z-[4] grid place-items-center px-gutter'>
                            <p className='hud max-w-[36ch] text-center text-mute'>
                                The live model needs WebGL, which this browser has switched off. The flags and scan notes remain available in the panels.
                            </p>
                        </div>
                    )}
                </div>

                <aside aria-label='Model controls' className='border-t border-hairline bg-panel desk:absolute desk:inset-y-0 desk:left-0 desk:z-[5] desk:w-[320px] desk:overflow-y-auto desk:border-r desk:border-t-0 desk:bg-panel/95'>
                    <div className='flex flex-col gap-8 py-8 pl-gutter pr-gutter desk:pr-6'>
                        <div>
                            <div data-ruler-mark='01' className='rise-fade' style={delay(0)}>
                                <p className='hud text-ink'>
                                    Fig. 01 / Live model / Interactive viewer
                                </p>

                                <p className='hud text-mute'>
                                    {building.name} / {building.city}
                                </p>
                            </div>

                            <h1 id='live-title' className='display-md mt-6'>
                                <span className='rise-line'>
                                    <span className='rise-inner text-ink' style={delay(80)}>
                                        Live model,
                                    </span>
                                </span>

                                <span className='rise-line'>
                                    <span className='rise-inner text-dim' style={delay(160)}>
                                        read it yourself.
                                    </span>
                                </span>
                            </h1>
                        </div>

                        <div role='group' aria-labelledby='live-building-label'>
                            <p id='live-building-label' className='hud mb-3 text-mute'>
                                Building
                            </p>

                            <div className='grid gap-2'>
                                {LIVE_BUILDINGS.map((item, index) => (
                                    <button
                                        key={item.slug}
                                        type='button'
                                        aria-pressed={buildingIndex === index}
                                        onClick={() => setBuildingIndex(index)}
                                        className={cn(
                                            'hud flex h-10 items-center justify-between border px-3 text-left transition-colors duration-300',
                                            buildingIndex === index ? 'border-ink bg-ink text-background' : 'border-hairline text-mute hover:border-mute hover:text-ink'
                                        )}
                                    >
                                        <span>
                                            {item.name}
                                        </span>

                                        <span className={buildingIndex === index ? 'text-background/70' : 'text-mute'}>
                                            {item.city}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div role='group' aria-labelledby='live-layers-label'>
                            <p id='live-layers-label' className='hud mb-3 text-mute'>
                                Layers
                            </p>

                            <div className='grid grid-cols-2 gap-2'>
                                {LAYERS.map(layer => (
                                    <button
                                        key={layer.id}
                                        type='button'
                                        aria-pressed={layers[layer.id]}
                                        title={layer.note}
                                        onClick={() => toggleLayer(layer.id)}
                                        className={cn(
                                            'hud flex h-10 items-center gap-2.5 border px-3 transition-colors duration-300',
                                            layers[layer.id] ? 'border-ink text-ink' : 'border-hairline text-mute hover:border-mute hover:text-ink'
                                        )}
                                    >
                                        <span className={cn('size-2 shrink-0 transition-opacity duration-300', SWATCH[layer.id], layers[layer.id] ? 'opacity-100' : 'opacity-30')} aria-hidden='true' />
                                        {layer.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className='mb-3 flex items-center justify-between gap-4'>
                                <label htmlFor='live-cut' className='hud text-mute'>
                                    Section cut
                                </label>

                                <button
                                    type='button'
                                    aria-pressed={cutOn}
                                    onClick={() => setCutOn(value => !value)}
                                    className={cn('hud h-7 border px-2.5 transition-colors duration-300', cutOn ? 'border-signal text-signal' : 'border-hairline text-mute hover:text-ink')}
                                >
                                    {cutOn ? 'On' : 'Off'}
                                </button>
                            </div>

                            <input
                                id='live-cut'
                                type='range'
                                min={0}
                                max={100}
                                value={cut}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    setCut(Number(event.target.value))
                                    setCutOn(true)
                                }}
                                aria-valuetext={`${cut} percent through the building`}
                                className='hud-range w-full'
                                style={{ '--fill': `${cut}%` } as CSSProperties}
                            />

                            <p className='hud mt-2 flex justify-between text-mute' aria-hidden='true'>
                                <span>
                                    East
                                </span>

                                <span className='text-ink'>
                                    {cut}%
                                </span>

                                <span>
                                    West
                                </span>
                            </p>
                        </div>

                        <div>
                            <div className='mb-3 flex items-center justify-between gap-4'>
                                <p className='hud text-mute'>
                                    Measure
                                </p>

                                <div className='flex gap-2'>
                                    {measure.step > 0 && (
                                        <button type='button' onClick={() => viewerRef.current?.clearMeasure()} className='hud h-7 border border-hairline px-2.5 text-mute hover:text-ink'>
                                            Clear
                                        </button>
                                    )}

                                    <button
                                        type='button'
                                        aria-pressed={measureMode}
                                        onClick={() => setMeasureMode(value => !value)}
                                        className={cn('hud h-7 border px-2.5 transition-colors duration-300', measureMode ? 'border-signal text-signal' : 'border-hairline text-mute hover:text-ink')}
                                    >
                                        {measureMode ? 'On' : 'Off'}
                                    </button>
                                </div>
                            </div>

                            <p className='hud border border-hairline px-3 py-2.5 text-ink' aria-live='polite'>
                                {!measureMode && measure.step === 0 && 'Turn on, then click two points'}
                                {measureMode && measure.step === 0 && 'Click the first point'}
                                {measure.step === 1 && 'Point A set / click a second point'}
                                {measure.step === 2 && measure.millimetres !== null && `A to B / ${String(measure.millimetres).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} mm`}
                            </p>
                        </div>

                        <div>
                            <div className='mb-3 flex items-baseline justify-between gap-4'>
                                <label htmlFor='live-epoch' className='hud text-mute'>
                                    Scan history
                                </label>

                                <output htmlFor='live-epoch' className='hud text-ink'>
                                    {epochInfo.date}
                                </output>
                            </div>

                            <input
                                id='live-epoch'
                                type='range'
                                min={0}
                                max={3}
                                step={1}
                                value={epoch}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setEpoch(Number(event.target.value))}
                                aria-valuetext={`${epochInfo.date}, ${epochInfo.label}`}
                                className='hud-range w-full'
                                style={{ '--fill': `${(epoch / 3) * 100}%` } as CSSProperties}
                            />

                            <div className='mt-2 grid grid-cols-4 gap-1' aria-hidden='true'>
                                {building.epochs.map((item, index) => (
                                    <button
                                        key={item.date}
                                        type='button'
                                        tabIndex={-1}
                                        onClick={() => setEpoch(index)}
                                        className={cn('hud text-left text-[9px] leading-[1.3] tracking-[0.06em] transition-colors duration-300', epoch === index ? 'text-ink' : 'text-mute hover:text-ink', index === 3 && 'text-right')}
                                    >
                                        {item.date.replace('September', 'Sep').replace('March', 'Mar')}
                                    </button>
                                ))}
                            </div>

                            <p className='mt-4 border-l border-signal pl-3 text-[14px] leading-[1.55] text-ink/85'>
                                <span className='hud block text-ink'>
                                    {epochInfo.label}
                                </span>
                                {epochInfo.note}
                            </p>
                        </div>
                    </div>
                </aside>

                <aside aria-label='Live flags' className='border-t border-hairline bg-panel desk:absolute desk:inset-y-0 desk:right-0 desk:z-[5] desk:w-[320px] desk:overflow-y-auto desk:border-l desk:border-t-0 desk:bg-panel/95'>
                    <div className='flex flex-col gap-6 py-8 pl-gutter pr-gutter desk:pl-6'>
                        <div className='flex items-baseline justify-between gap-4'>
                            <p className='hud text-ink'>
                                Live flags / {String(building.flags.length).padStart(2, '0')}
                            </p>

                            <button
                                type='button'
                                onClick={() => {
                                    setFocused(null)
                                    viewerRef.current?.focusFlag(null)
                                }}
                                className='hud inline-flex items-center gap-2 text-mute hover:text-ink'
                            >
                                <RotateCcw className='size-3.5' strokeWidth={1.5} aria-hidden='true' />
                                Reset view
                            </button>
                        </div>

                        <ul className='border-t border-hairline'>
                            {building.flags.map((flag, index) => {
                                const isFocused = focused === index

                                return (
                                    <li key={flag.id} className='relative border-b border-hairline'>
                                        <span className={cn('absolute inset-y-0 left-0 w-[3px] origin-top bg-signal transition-transform duration-500 ease-instrument', isFocused ? 'scale-y-100' : 'scale-y-0')} aria-hidden='true' />

                                        <button
                                            type='button'
                                            aria-pressed={isFocused}
                                            onClick={() => focusFlag(index)}
                                            className='w-full py-4 pl-4 pr-1 text-left'
                                        >
                                            <span className='hud flex items-center justify-between gap-3'>
                                                <span className='text-signal'>
                                                    {flag.id}
                                                </span>

                                                <span className='text-mute'>
                                                    {flag.status}
                                                </span>
                                            </span>

                                            <span className='hud mt-2 block text-ink'>
                                                {flag.text}
                                            </span>

                                            <span className={cn('grid transition-[grid-template-rows] duration-500 ease-instrument', isFocused ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
                                                <span className='overflow-hidden'>
                                                    <span className='block pt-3 text-[14px] leading-[1.55] text-ink/80'>
                                                        {flag.note}
                                                    </span>
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                )
                            })}
                        </ul>

                        <dl className='border-t border-hairline'>
                            {[
                                { label: 'Building', value: building.name },
                                { label: 'Scan', value: `${epochInfo.date} / ${epochInfo.label}` },
                                { label: 'Layers on', value: LAYERS.filter(layer => layers[layer.id]).map(layer => layer.label).join(', ') || 'None' },
                                { label: 'Section', value: cutOn ? `${cut}% from east` : 'Off' }
                            ].map(row => (
                                <div key={row.label} className='flex items-baseline justify-between gap-4 border-b border-hairline py-2.5'>
                                    <dt className='hud text-mute'>
                                        {row.label}
                                    </dt>

                                    <dd className='hud text-right text-ink'>
                                        {row.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <p className='text-[13px] leading-[1.55] text-mute'>
                            Heat is drawn in blue, everything else in cream and grey. Settlement in the September 2023 and March 2024 scans is exaggerated 50 times so a few millimetres can be seen.
                        </p>
                    </div>
                </aside>
            </section>
        </Layout>
    )
}

export default LiveModelPage
